import React, { useState } from 'react';
import { MessageSquare, X } from 'lucide-react';

const FeedbackModal = ({ isOpen, onClose, t, authenticatedFetch }) => {
    const [feedback, setFeedback] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

    const handleSubmit = async () => {
        if (!feedback.trim()) return;

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const response = await authenticatedFetch('/api/feedback', {
                method: 'POST',
                body: JSON.stringify({ feedback: feedback.trim() })
            });

            if (response.ok) {
                setSubmitStatus('success');
                setTimeout(() => {
                    setFeedback('');
                    setSubmitStatus(null);
                    onClose();
                }, 2000);
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Failed to submit feedback:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center gap-3">
                            <MessageSquare className="w-6 h-6 text-sky-600" />
                            <h2 className="text-2xl font-bold text-gray-800">
                                {t('feedbackModalTitle')}
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 mb-6">
                        {t('feedbackDescription')}
                    </p>

                    {/* Instructions */}
                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-6">
                        <p className="font-semibold text-gray-700 mb-3">
                            {t('feedbackInstructions')}
                        </p>
                        <div className="space-y-2 text-sm text-gray-600">
                            <p>{t('feedbackTip1')}</p>
                            <p>{t('feedbackTip2')}</p>
                            <p>{t('feedbackTip3')}</p>
                            <p>{t('feedbackTip4')}</p>
                            <p>{t('feedbackTip5')}</p>
                        </div>
                    </div>

                    {/* Feedback Input */}
                    <textarea
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder={t('feedbackPlaceholder')}
                        className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400 resize-none"
                        rows={8}
                        disabled={isSubmitting}
                    />

                    {/* Status Messages */}
                    {submitStatus === 'success' && (
                        <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                            {t('feedbackSuccess')}
                        </div>
                    )}
                    {submitStatus === 'error' && (
                        <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                            {t('feedbackError')}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            disabled={isSubmitting}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting || !feedback.trim()}
                            className="px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                            {isSubmitting ? t('submittingFeedback') : t('submitFeedback')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeedbackModal;